/**
 * Estimated legend block height before `onLayout` reports a real measurement.
 * Matches typical two-row legend + spacing in the member app spot-booking sheet.
 *
 * Covers the legend only. The friend slot chips are not included: they sit above the grid and are
 * subtracted from their own measured `friendSlotsHeight`, which is 0 both while unmeasured and when
 * friends mode is off, so this module cannot tell the two apart to estimate one.
 */
export const SPOT_LEGEND_HEIGHT_ESTIMATE_PX = 160;

export const SPOT_LEGEND_SPACER_MARGIN_TOP = 16;

export const SPOT_LEGEND_SPACER_PADDING_BOTTOM = 8;

export const SPOT_LEGEND_SPACER_PADDING_HORIZONTAL = 8;

/** Approximate maximum card height that exposes three wrapped legend content rows. */
export const SPOT_LEGEND_THREE_ROW_MAX_HEIGHT_PX = 140;

/** Maximum share of a fixed map viewport reserved for the legend card. */
export const SPOT_LEGEND_FIXED_VIEWPORT_FRACTION = 0.25;

/**
 * Automatic legend-card cap for a fixed pinch viewport. Short legends keep their natural height;
 * taller legends scroll inside this cap. The absolute ceiling prevents large screens from showing
 * more than approximately three wrapped content rows.
 */
export function getFixedViewportLegendMaxHeight(pinchViewportHeight: number): number | undefined {
  if (!Number.isFinite(pinchViewportHeight) || pinchViewportHeight <= 0) return undefined;
  return Math.min(
    SPOT_LEGEND_THREE_ROW_MAX_HEIGHT_PX,
    pinchViewportHeight * SPOT_LEGEND_FIXED_VIEWPORT_FRACTION
  );
}

/**
 * Height the zoom viewport may take inside the measured pinch overlay.
 *
 * The overlay also holds the friend slot chips (friends mode) above the grid and the legend below
 * it, so both are subtracted before the grid gets what is left. `friendSlotsHeight` is the measured
 * height of the chips wrapper (0 when friends mode is off), and it already includes the chips'
 * bottom margin because that margin sits inside the wrapper's content box. Fixed viewports pass
 * their legend card cap as `unmeasuredLegendHeight` so the initial reserve matches the maximum
 * height that can be measured after layout. Page-scroll embedding keeps the uncapped estimate.
 */
export function getPinchZoomMaxSlotHeight(
  pinchOverlayHeight: number,
  legendHeight: number,
  hideLegend = false,
  friendSlotsHeight = 0,
  unmeasuredLegendHeight = SPOT_LEGEND_HEIGHT_ESTIMATE_PX
): number {
  if (pinchOverlayHeight <= 0) return 0;
  const availableHeight = pinchOverlayHeight - Math.max(0, friendSlotsHeight);
  if (hideLegend) return Math.max(0, availableHeight);
  if (legendHeight > 0) {
    // Measured legendSpacer height includes paddingBottom; marginTop is outside the layout box.
    return Math.max(0, availableHeight - legendHeight - SPOT_LEGEND_SPACER_MARGIN_TOP);
  }
  const legendReserve =
    Math.max(0, unmeasuredLegendHeight) +
    SPOT_LEGEND_SPACER_MARGIN_TOP +
    SPOT_LEGEND_SPACER_PADDING_BOTTOM;
  return Math.max(0, availableHeight - legendReserve);
}
